import { useEffect, useRef } from 'react';
import { useLandmarkStore } from '../state/landmarks';

/**
 * Tiny floating menu shown at the right-click location. One action for now
 * ("Add POI here"). Closes on any outside click or Escape.
 */
export function MapContextMenu() {
  const contextMenu = useLandmarkStore((state) => state.contextMenu);
  const closeContextMenu = useLandmarkStore((state) => state.closeContextMenu);
  const openNewPOIForm = useLandmarkStore((state) => state.openNewPOIForm);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contextMenu === null) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        closeContextMenu();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeContextMenu();
    };
    // Defer attaching mousedown so the contextmenu event itself doesn't
    // immediately close the menu we just opened.
    const id = window.setTimeout(() => {
      window.addEventListener('mousedown', onDown);
    }, 0);
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [contextMenu, closeContextMenu]);

  const isAdmin = useLandmarkStore((state) => state.isAdmin);

  if (contextMenu === null) return null;

  // Position the menu near the click. Clamp inside the viewport so it doesn't
  // overflow at the edges.
  const { screenX, screenY, lng, lat } = contextMenu;
  const margin = 8;
  const width = 200;
  const height = 88;
  const left = Math.min(Math.max(margin, screenX), window.innerWidth - width - margin);
  const top = Math.min(Math.max(margin, screenY), window.innerHeight - height - margin);

  return (
    <div
      ref={ref}
      role="menu"
      data-testid="map-context-menu"
      style={{
        position: 'fixed',
        left,
        top,
        width,
        zIndex: 90,
        background: 'var(--surface-raised)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-elevated)',
        overflow: 'hidden',
        fontFamily: 'var(--font-body)',
      }}
    >
      {isAdmin ? (
        <button
          type="button"
          onClick={() => openNewPOIForm([lng, lat])}
          data-testid="map-context-add-poi"
          style={{
            width: '100%',
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            background: 'transparent',
            color: 'var(--text)',
            fontSize: '0.9rem',
            fontWeight: 600,
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          <svg
            width={16}
            height={16}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add POI here
        </button>
      ) : (
        <div
          style={{
            padding: '10px 12px',
            fontSize: '0.85rem',
            color: 'var(--text-muted)',
          }}
        >
          Sign in as admin to add POIs.
        </div>
      )}
      <div
        style={{
          padding: '6px 12px',
          fontSize: '0.7rem',
          color: 'var(--text-muted)',
          borderTop: '1px solid var(--border)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {lat.toFixed(5)}, {lng.toFixed(5)}
      </div>
    </div>
  );
}
